/* Unbound Resonance reviews (storefront).
   <urrv-hub>    : Reviews page. Fetches one page at a time from the review server's public feed
                   (/apps/rewards/reviews/feed), which does all filtering, counting and sorting.
                   Filter state lives in the URL (?rating=5&size=L&type=hoodie&sort=highest).
   <urrv-list>   : "Show more" for server-rendered lists (reviewer profile).
   Photo viewer  : any [data-urrv-photo] button opens the shared <dialog data-urrv-lightbox>.
   Review text and names are only ever set with textContent; the tier marks and stars are cloned
   from <template>s the theme rendered, so no customer content is parsed as HTML. */
(() => {
  const SORTS = ['recent', 'highest', 'lowest'];
  const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
  const el = (tag, cls, text) => {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  };
  const fmtDate = (iso) => {
    const d = new Date(`${iso}T12:00:00Z`);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  };
  // Only same-site paths from the server are used as links.
  const safePath = (p) => (typeof p === 'string' && /^\/(products|pages\/reviewer)\/[A-Za-z0-9._~-]+$/.test(p) ? p : null);
  const safeImg = (u) => { try { const x = new URL(u); return x.protocol === 'https:' ? x.toString() : null; } catch (_) { return null; } };

  /* ---------- Photo viewer (shared) ---------- */
  if (!window.__urrvLightbox) {
    window.__urrvLightbox = true;
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-urrv-photo]');
      const box = document.querySelector('[data-urrv-lightbox]');
      if (!box) return;
      if (btn) {
        const full = safeImg(btn.dataset.full);
        if (!full) return;
        const img = box.querySelector('[data-urrv-lightbox-img]');
        img.src = full;
        box.__opener = btn;
        box.showModal();
        document.documentElement.classList.add('urrv-lock');
        return;
      }
      if (box.open && (e.target === box || e.target.closest('[data-urrv-lightbox-close]'))) box.close();
    });
    document.addEventListener('close', (e) => {
      if (!e.target.matches || !e.target.matches('[data-urrv-lightbox]')) return;
      document.documentElement.classList.remove('urrv-lock');
      e.target.__opener?.focus({ preventScroll: true });
    }, true);
  }

  /* ---------- Pause off-screen tier marks (they animate) ---------- */
  const io = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => entries.forEach((en) => en.target.classList.toggle('is-paused', !en.isIntersecting)), { rootMargin: '150px' })
    : null;
  const watchMarks = (root) => io && root.querySelectorAll('.ur-mark').forEach((m) => io.observe(m));

  /* ---------- <urrv-list>: show more ---------- */
  if (!customElements.get('urrv-list')) {
    customElements.define('urrv-list', class extends HTMLElement {
      connectedCallback() {
        watchMarks(this);
        const btn = this.querySelector('[data-urrv-list-more]');
        if (!btn || this.bound) return;
        this.bound = true;
        const step = Number(this.dataset.step) || 12;
        btn.addEventListener('click', () => {
          const hidden = [...this.querySelectorAll('[data-urrv-item][hidden]')];
          hidden.slice(0, step).forEach((li, i) => {
            li.hidden = false;
            if (i === 0) { const h = li.querySelector('h3'); h?.setAttribute('tabindex', '-1'); h?.focus(); }
          });
          if (hidden.length <= step) btn.closest('.urrv-more').hidden = true;
        });
      }
    });
  }

  /* ---------- <urrv-hub>: Reviews page ---------- */
  if (customElements.get('urrv-hub')) return;
  customElements.define('urrv-hub', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.endpoint = this.dataset.endpoint || '/apps/rewards/reviews/feed';
      this.q = (s) => this.querySelector(s);
      this.els = {
        results: this.q('[data-urrv-results]'), list: this.q('[data-urrv-list]'), count: this.q('[data-urrv-count]'),
        sort: this.q('[data-urrv-sort]'), form: this.q('[data-urrv-form]'), active: this.q('[data-urrv-active]'),
        chips: this.q('[data-urrv-chips]'), more: this.q('[data-urrv-more]'), empty: this.q('[data-urrv-empty]'),
        none: this.q('[data-urrv-none]'), error: this.q('[data-urrv-error]'), score: this.q('[data-urrv-score]'),
        panel: this.q('[data-urrv-filters]'), scrim: this.q('[data-urrv-scrim]'), toggle: this.q('[data-urrv-open]'),
        apply: this.q('[data-urrv-apply]'),
      };
      this.state = this.readUrl();
      this.page = 1;

      this.els.sort.addEventListener('change', () => this.update({ sort: this.els.sort.value }));
      this.els.form.addEventListener('change', (e) => {
        const t = e.target;
        if (t.name === 'rating' || t.name === 'size' || t.name === 'type') this.update({ [t.name]: t.value || null });
      });
      this.addEventListener('click', (e) => {
        const chip = e.target.closest('[data-urrv-unset]');
        if (chip) return this.update({ [chip.dataset.urrvUnset]: null });
        if (e.target.closest('[data-urrv-clear]')) return this.update({ rating: null, size: null, type: null });
        if (e.target.closest('[data-urrv-retry]')) return this.load(this.page > 1);
        if (e.target.closest('[data-urrv-more] button')) { this.page += 1; return this.load(true); }
        if (e.target.closest('[data-urrv-open]')) return this.openPanel();
        if (e.target.closest('[data-urrv-close]') || e.target.closest('[data-urrv-apply]') || e.target === this.els.scrim) return this.closePanel();
        return undefined;
      });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.els.panel.classList.contains('is-open')) this.closePanel(); });
      window.addEventListener('popstate', () => { this.state = this.readUrl(); this.page = 1; this.load(false); });
      this.load(false);
    }

    readUrl() {
      const p = new URLSearchParams(location.search);
      const rating = /^[1-5]$/.test(p.get('rating') || '') ? p.get('rating') : null;
      const size = /^[A-Za-z0-9./ -]{1,12}$/.test(p.get('size') || '') ? p.get('size').toUpperCase() : null;
      const type = /^[a-z0-9 -]{1,40}$/.test(p.get('type') || '') ? p.get('type') : null;
      const sort = SORTS.includes(p.get('sort')) ? p.get('sort') : 'recent';
      return { rating, size, type, sort };
    }

    params(page) {
      const p = new URLSearchParams();
      ['rating', 'size', 'type'].forEach((k) => { if (this.state[k]) p.set(k, this.state[k]); });
      if (this.state.sort !== 'recent') p.set('sort', this.state.sort);
      if (page) p.set('page', String(page));
      return p;
    }

    update(patch) {
      this.state = { ...this.state, ...patch };
      this.page = 1;
      const qs = this.params().toString();
      history.pushState(null, '', `${location.pathname}${qs ? `?${qs}` : ''}`);
      this.load(false);
    }

    async load(append) {
      this.ctrl?.abort();
      this.ctrl = new AbortController();
      const { results, list, error, empty, none, more } = this.els;
      results.setAttribute('aria-busy', 'true');
      error.hidden = true;
      if (!append) { empty.hidden = true; none.hidden = true; if (!list.children.length) this.skeleton(); }
      try {
        const res = await fetch(`${this.endpoint}?${this.params(this.page)}`, { headers: { Accept: 'application/json' }, signal: this.ctrl.signal });
        const data = await res.json();
        if (!res.ok || !data.ok) throw new Error('bad response');
        this.render(data, append);
      } catch (err) {
        if (err.name === 'AbortError') return;
        if (!append) list.replaceChildren();
        more.hidden = true;
        error.hidden = false;
        this.els.count.textContent = '';
      } finally {
        results.setAttribute('aria-busy', 'false');
      }
    }

    skeleton() {
      const tpl = this.q('template[data-urrv-skeleton]');
      this.els.list.replaceChildren(...Array.from({ length: 3 }, () => tpl.content.cloneNode(true)));
    }

    render(d, append) {
      const { list, count, more, empty, none, score, sort, active, chips } = this.els;
      // Header stats: real totals from the server.
      if (d.stats.total > 0) {
        score.hidden = false;
        this.q('[data-urrv-avg]').textContent = d.stats.average.toFixed(1);
        this.q('[data-urrv-avg-stars]').replaceChildren(this.stars(Math.round(d.stats.average)));
        this.q('[data-urrv-avg-label]').textContent = `Average rating ${d.stats.average.toFixed(1)} out of 5`;
        this.q('[data-urrv-total]').textContent = plural(d.stats.total, 'Review', 'Reviews');
      } else {
        score.hidden = true;
      }
      sort.value = d.filters.sort;
      this.renderFacets(d);

      const items = d.reviews.map((r) => { const li = el('li'); li.append(this.card(r)); return li; });
      if (append) list.append(...items); else list.replaceChildren(...items);
      watchMarks(list);

      count.textContent = `Showing ${plural(d.matching, 'review', 'reviews')}`;
      this.els.apply.textContent = `Show ${plural(d.matching, 'review', 'reviews')}`;
      more.hidden = !d.hasMore;
      const filtered = Boolean(d.filters.rating || d.filters.size || d.filters.type);
      empty.hidden = !(filtered && d.matching === 0);
      none.hidden = !(!filtered && d.stats.total === 0);

      // Active filter chips.
      const labels = [];
      if (d.filters.rating) labels.push(['rating', plural(d.filters.rating, 'Star', 'Stars')]);
      if (d.filters.size) labels.push(['size', `Size ${d.filters.size}`]);
      if (d.filters.type) {
        const t = d.facets.type.find((x) => x.value === d.filters.type);
        labels.push(['type', t ? t.label : d.filters.type]);
      }
      chips.replaceChildren(...labels.map(([key, text]) => {
        const b = el('button', 'urrv-chip');
        b.type = 'button';
        b.dataset.urrvUnset = key;
        b.setAttribute('aria-label', `Remove filter: ${text}`);
        b.append(el('span', null, text), this.q('template[data-urrv-x]').content.cloneNode(true));
        return b;
      }));
      active.hidden = labels.length === 0;
    }

    renderFacets(d) {
      const group = (name, options, selected) => {
        const box = this.q(`[data-urrv-opts="${name}"]`);
        const id = (v) => `urrv-${this.id || 'hub'}-${name}-${String(v).replace(/[^A-Za-z0-9]/g, '_')}`;
        const make = (value, labelNodes, n, disabled) => {
          const wrap = el('div', 'urrv-opt');
          const input = el('input');
          Object.assign(input, { type: 'radio', name, value, id: id(value || 'all'), checked: String(selected || '') === String(value), disabled });
          const label = el('label');
          label.htmlFor = input.id;
          label.append(...labelNodes);
          if (n != null) label.append(el('span', 'urrv-opt__count', String(n)));
          wrap.append(input, label);
          return wrap;
        };
        const nodes = [make('', [el('span', null, name === 'rating' ? 'All ratings' : name === 'size' ? 'All sizes' : 'All clothing')], null, false)];
        options.forEach((o) => nodes.push(make(String(o.value), o.label, o.count, o.count === 0 && String(selected) !== String(o.value))));
        box.replaceChildren(...nodes);
        box.closest('[data-urrv-group]').hidden = name !== 'rating' && options.length === 0;
      };
      group('rating', d.facets.rating.map((r) => {
        const s = this.stars(r.value);
        s.querySelector('[role="img"]')?.setAttribute('aria-label', plural(r.value, 'star', 'stars'));
        return { value: r.value, count: r.count, label: [s] };
      }), d.filters.rating);
      const withSelected = (arr, sel, mk) => (sel && !arr.some((x) => x.value === sel) ? [...arr, mk(sel)] : arr);
      group('size', withSelected(d.facets.size, d.filters.size, (v) => ({ value: v, count: 0 })).map((s) => ({ ...s, label: [el('span', null, s.value)] })), d.filters.size);
      group('type', withSelected(d.facets.type, d.filters.type, (v) => ({ value: v, label: v, count: 0 })).map((t) => ({ value: t.value, count: t.count, label: [el('span', null, t.label)] })), d.filters.type);
    }

    stars(n) {
      const t = this.q(`template[data-urrv-stars="${Math.min(5, Math.max(1, n))}"]`);
      return t ? t.content.cloneNode(true) : document.createDocumentFragment();
    }

    tier(code) {
      const t = code && this.q(`template[data-urrv-tier="${code}"]`);
      return t ? t.content.cloneNode(true) : document.createDocumentFragment();
    }

    card(r) {
      const art = el('article', 'urrv-card');
      const hid = `urrv-c-${r.id.replace(/[^A-Za-z0-9_-]/g, '')}`;
      art.setAttribute('aria-labelledby', hid);

      const head = el('header', 'urrv-card__head');
      const who = el('div', 'urrv-who');
      const h = el('h3', 'urrv-who__name');
      h.id = hid;
      h.append(el('span', 'urfr-vh', 'Review by '));
      const rvUrl = safePath(r.reviewer.url);
      if (rvUrl) { const a = el('a', 'urrv-who__link', r.reviewer.name); a.href = rvUrl; h.append(a); } else h.append(document.createTextNode(r.reviewer.name));
      who.append(h, this.tier(r.reviewer.tier));
      head.append(who, this.stars(r.rating));
      art.append(head);
      if (r.verified) art.append(el('p', 'urrv-verified', 'Verified purchase'));

      const pUrl = safePath(r.product.url);
      const prod = el(pUrl ? 'a' : 'div', 'urrv-product');
      if (pUrl) prod.href = pUrl;
      const img = safeImg(r.product.image);
      if (img) { const i = el('img', 'urrv-product__img'); Object.assign(i, { src: img, alt: '', loading: 'lazy', width: 56, height: 56 }); prod.append(i); } else prod.append(el('span', 'urrv-product__img'));
      const meta = el('span', 'urrv-product__meta');
      meta.append(el('span', 'urrv-product__t', r.product.title));
      const sub = pUrl ? (r.type ? r.type.name : '') : 'Past drop';
      if (sub) meta.append(el('span', 'urrv-product__s', sub));
      prod.append(meta);
      art.append(prod);

      const fit = el('div', 'urrv-fit');
      fit.setAttribute('role', 'group');
      fit.setAttribute('aria-label', 'Fit details');
      fit.append(el('p', 'urrv-fit__h', 'Fit details'));
      const dl = el('dl');
      [['Size', r.size], ['Height', r.height], ['Weight', r.weight]].forEach(([k, val]) => {
        if (!val) return;
        const row = el('div');
        row.append(el('dt', null, k), el('dd', null, val));
        dl.append(row);
      });
      fit.append(dl);
      art.append(fit, el('div', 'urrv-text', r.text));

      const full = r.photo && safeImg(r.photo.full);
      const thumb = r.photo && safeImg(r.photo.thumb);
      if (full && thumb) {
        const ph = el('div', 'urrv-photos');
        const b = el('button', 'urrv-photo');
        b.type = 'button';
        b.dataset.urrvPhoto = '';
        b.dataset.full = full;
        b.setAttribute('aria-label', 'View customer photo larger');
        const i = el('img');
        Object.assign(i, { src: thumb, alt: 'Customer photo', loading: 'lazy' });
        if (r.photo.width && r.photo.height) { i.width = r.photo.width; i.height = r.photo.height; }
        b.append(i);
        ph.append(b);
        art.append(ph);
      }

      const foot = el('footer', 'urrv-card__foot');
      if (r.date) { const t = el('time', null, fmtDate(r.date)); t.dateTime = r.date; foot.append(t); }
      if (pUrl) { const a = el('a', 'urrv-link', 'View product'); a.href = pUrl; foot.append(a); }
      art.append(foot);
      return art;
    }

    openPanel() {
      const { panel, scrim, toggle } = this.els;
      panel.classList.add('is-open');
      scrim.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('urrv-lock');
      panel.querySelector('[data-urrv-close]')?.focus();
    }

    closePanel() {
      const { panel, scrim, toggle } = this.els;
      if (!panel.classList.contains('is-open')) return;
      panel.classList.remove('is-open');
      scrim.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('urrv-lock');
      toggle.focus({ preventScroll: true });
    }
  });
})();